import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { ArrowUpRight, ListChecks, Maximize2 } from "lucide-react";
import { z } from "zod";

import { useTasksList, useUpdateTask } from "@/application/use-cases/tasks/use-tasks.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import { TaskDrawerContent } from "@/presentation/components/tasks/task-drawer-content";
import { RouteHero } from "@/presentation/components/shell/route-hero";
import {
  PRIORITY_LABEL,
  StatusDot,
  TASK_STATUS_LABEL,
  priorityToTone,
  taskStatusToTone,
} from "@/presentation/components/status/status-dot";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@monorepo-apps/ui/src/components/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@monorepo-apps/ui/src/components/sheet";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import type {
  Priority,
  StakeholderRow,
  TaskRow,
  TaskStatus,
} from "@/infrastructure/supabase/database.types";

const STATUS_ORDER: TaskStatus[] = ["in_progress", "todo", "blocked", "done"];

const tasksSearchSchema = z.object({
  id: z.string().optional(),
});

export const Route = createFileRoute("/_app/tasks/")({
  validateSearch: tasksSearchSchema,
  component: TasksListPage,
});

function TasksListPage() {
  const tasksQ = useTasksList();
  const stakeholdersQ = useStakeholdersList();
  const updateTask = useUpdateTask();
  const navigate = useNavigate({ from: Route.fullPath });
  const { id: openId } = Route.useSearch();
  const tasks = tasksQ.data ?? [];
  const stakeholders = stakeholdersQ.data ?? [];
  const assigneesById = new Map(stakeholders.map((s) => [s.id, s.name]));

  const counts: Record<TaskStatus, number> = {
    todo: 0,
    in_progress: 0,
    done: 0,
    blocked: 0,
  };
  for (const t of tasks) counts[t.status]++;

  const sorted = [...tasks].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
  );

  return (
    <div className="animate-page-enter mx-auto flex w-full max-w-7xl flex-col gap-8 p-6 lg:gap-10 lg:p-10">
      <RouteHero
        eyebrow="Work in Flight"
        icon={ListChecks}
        numeral={String(counts.in_progress).padStart(2, "0")}
        numeralTone="amber"
        numeralSuffix={`/ ${String(tasks.length).padStart(2, "0")}`}
        title="Tasks moving the assessment forward"
        summary="What's in progress, what's queued, what's blocked. Tap a row to inspect or reassign."
        meta={[
          { label: "In Progress", value: counts.in_progress, tone: "amber" },
          { label: "To Do", value: counts.todo },
          { label: "Blocked", value: counts.blocked, tone: "red" },
          { label: "Done", value: counts.done, tone: "green" },
        ]}
        actions={
          <Link
            to="/tasks/new"
            className="group inline-flex items-center justify-between gap-2 rounded-lg border border-[oklch(1_0_0_/_0.12)] bg-[oklch(1_0_0_/_0.04)] px-4 py-2.5 text-[13px] font-medium text-[oklch(0.93_0.008_240)] transition-all hover:border-[oklch(0.72_0.17_190_/_0.5)] hover:bg-[oklch(0.72_0.17_190_/_0.08)] print:hidden"
          >
            <span className="font-data text-[11px] tracking-[0.12em] uppercase">+ New task</span>
            <ArrowUpRight className="size-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        }
      />

      <div>
        {sorted.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-xs italic">
            No tasks yet — create one to start tracking work.
          </p>
        ) : (
          <div className="glow-card-static p-4 lg:p-6">
            <table className="w-full text-xs">
              <thead className="text-muted-foreground font-data text-left text-[10px] tracking-[0.18em] uppercase">
                <tr>
                  <th className="py-2 pr-4 font-medium">Task</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Priority</th>
                  <th className="py-2 pr-4 font-medium">Assignee</th>
                  <th className="py-2 pr-4 font-medium">Due</th>
                  <th className="py-2 font-medium print:hidden" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => (
                  <TaskRowItem
                    key={t.id}
                    task={t}
                    stakeholders={stakeholders}
                    assigneeName={t.assignee_id ? (assigneesById.get(t.assignee_id) ?? null) : null}
                    onTitleCommit={(next) => {
                      const trimmed = next.trim();
                      if (trimmed && trimmed !== t.title) {
                        updateTask.mutate({ id: t.id, patch: { title: trimmed } });
                      }
                    }}
                    onStatusChange={(status) => updateTask.mutate({ id: t.id, patch: { status } })}
                    onPriorityChange={(priority) =>
                      updateTask.mutate({ id: t.id, patch: { priority } })
                    }
                    onAssigneeChange={(assigneeId) =>
                      updateTask.mutate({ id: t.id, patch: { assignee_id: assigneeId } })
                    }
                    onOpen={() => navigate({ search: { id: t.id } })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Sheet
        open={!!openId}
        onOpenChange={(open) => {
          if (!open) navigate({ search: {} });
        }}
      >
        <SheetContent side="right" className="w-full p-0 sm:max-w-2xl">
          <SheetTitle className="sr-only">Task detail</SheetTitle>
          <SheetDescription className="sr-only">View and edit the full task.</SheetDescription>
          {openId ? <TaskDrawerContent id={openId} /> : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function TaskRowItem({
  task,
  stakeholders,
  assigneeName,
  onTitleCommit,
  onStatusChange,
  onPriorityChange,
  onAssigneeChange,
  onOpen,
}: {
  task: TaskRow;
  stakeholders: StakeholderRow[];
  assigneeName: string | null;
  onTitleCommit: (next: string) => void;
  onStatusChange: (status: TaskStatus) => void;
  onPriorityChange: (priority: Priority) => void;
  onAssigneeChange: (assigneeId: string | null) => void;
  onOpen: () => void;
}) {
  return (
    <tr className="border-border/40 hover:bg-muted/30 group border-t print:break-inside-avoid">
      <td className="py-1.5 pr-4">
        <input
          key={task.title}
          type="text"
          defaultValue={task.title}
          aria-label="Edit task title"
          className="focus:border-ring focus:bg-background hover:border-border/60 w-full truncate rounded-sm border border-transparent bg-transparent px-1.5 py-1 text-xs transition outline-none"
          onBlur={(e) => onTitleCommit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            } else if (e.key === "Escape") {
              e.currentTarget.value = task.title;
              e.currentTarget.blur();
            }
          }}
        />
      </td>
      <td className="py-1.5 pr-4">
        <Select value={task.status} onValueChange={(v) => onStatusChange(v as TaskStatus)}>
          <SelectTrigger
            className="hover:border-border/60 h-7 gap-1.5 border-transparent bg-transparent px-2 [&>svg:last-child]:opacity-50"
            aria-label="Change status"
          >
            <div className="inline-flex items-center gap-2">
              <StatusDot tone={taskStatusToTone(task.status)} />
              <span className="text-[11px]">{TASK_STATUS_LABEL[task.status]}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {(["todo", "in_progress", "done", "blocked"] as TaskStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                <span className="inline-flex items-center gap-2">
                  <StatusDot tone={taskStatusToTone(s)} /> {TASK_STATUS_LABEL[s]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 pr-4">
        <Select value={task.priority} onValueChange={(v) => onPriorityChange(v as Priority)}>
          <SelectTrigger
            className="hover:border-border/60 h-7 gap-1.5 border-transparent bg-transparent px-2 [&>svg:last-child]:opacity-50"
            aria-label="Change priority"
          >
            <div className="inline-flex items-center gap-2">
              <StatusDot tone={priorityToTone(task.priority)} />
              <span className="text-[10px] tracking-wide uppercase">{task.priority}</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {(["low", "medium", "high"] as Priority[]).map((p) => (
              <SelectItem key={p} value={p}>
                <span className="inline-flex items-center gap-2">
                  <StatusDot tone={priorityToTone(p)} /> {PRIORITY_LABEL[p]}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 pr-4">
        <Select
          value={task.assignee_id ?? "__none__"}
          onValueChange={(v) => onAssigneeChange(v === "__none__" ? null : v)}
        >
          <SelectTrigger
            className="hover:border-border/60 h-7 gap-1.5 border-transparent bg-transparent px-2 [&>svg:last-child]:opacity-50"
            aria-label="Change assignee"
          >
            <span className={cn("text-xs", !assigneeName && "text-muted-foreground")}>
              {assigneeName ?? "Unassigned"}
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">
              <span className="text-muted-foreground">Unassigned</span>
            </SelectItem>
            {stakeholders.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="inline-flex items-center gap-2">
                  {s.name}
                  <span className="text-muted-foreground text-[10px] uppercase">· {s.org}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="text-muted-foreground font-data py-2 pr-4 text-[11px] tabular-nums">
        {task.due_date ? format(new Date(task.due_date), "d MMM") : "—"}
      </td>
      <td className="py-2 print:hidden">
        <button
          type="button"
          onClick={onOpen}
          className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-7 items-center justify-center rounded-sm opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
          aria-label="Open task detail"
          title="Open detail"
        >
          <Maximize2 className="size-3.5" />
        </button>
      </td>
    </tr>
  );
}
